/**
 * Role-scoped navigation items.
 * Each item specifies which Cognito groups can see it.
 */

import type { RoleDefinition } from '@api/endpoints/onboardingApi';
import { COMPONENT_CATALOG } from '@lib/componentCatalog';

export interface NavItem {
  label: string;
  path: string;
  icon: string; // lucide icon name
  allowedGroups: string[];
}

// Completed authoritative nav map (Component Access Control, Decision 8,
// Req 19.5, 19.6). One entry per Component_Catalog `navPath` plus the
// configuration / landing surfaces. Each item's `allowedGroups` agrees exactly
// with STATIC_ROLE_ROUTE_MAP (no Component reachable via nav but denied by the
// route map, or vice versa). Retained ONLY as the Default_Access_Policy seed
// source (Req 10.3); runtime nav is policy-driven (Req 10.2).
//
// standard-user sees /alarms, /worklists, /goal-sets, /reminders but NOT
// /metrics (practice-owner only); data_uploader sees only the Data surfaces and
// is intentionally absent from /home.
export const STATIC_NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', path: '/home', icon: 'Home', allowedGroups: ['medrender-owner', 'practice-owner', 'standard-user'] },
  { label: 'Surveys', path: '/surveys', icon: 'ClipboardList', allowedGroups: ['medrender-owner', 'practice-owner', 'standard-user'] },
  { label: 'Visits', path: '/visits', icon: 'Stethoscope', allowedGroups: ['medrender-owner', 'practice-owner', 'standard-user'] },
  { label: 'Provider Scorecards', path: '/provider-scorecard', icon: 'Award', allowedGroups: ['medrender-owner', 'practice-owner', 'standard-user'] },
  // Visit Alarms is a practice-owner-only feature (visit-alarms Req 7.1, 12.3):
  // owner-only visibility mirrors Metrics (no standard-user), and it is a
  // non-catalog nav path so it keeps role-based visibility rather than being
  // gated by the per-practice Access_Matrix.
  { label: 'Visit Alarms', path: '/visit-alarms', icon: 'BellRing', allowedGroups: ['medrender-owner', 'practice-owner'] },
  { label: 'Metrics', path: '/metrics', icon: 'LineChart', allowedGroups: ['medrender-owner', 'practice-owner'] },
  { label: 'Alarms & Findings', path: '/alarms', icon: 'Siren', allowedGroups: ['medrender-owner', 'practice-owner', 'standard-user'] },
  { label: 'Worklists', path: '/worklists', icon: 'ListChecks', allowedGroups: ['medrender-owner', 'practice-owner', 'standard-user'] },
  { label: 'Goals', path: '/goal-sets', icon: 'Target', allowedGroups: ['medrender-owner', 'practice-owner', 'standard-user'] },
  { label: 'Reminders', path: '/reminders', icon: 'Bell', allowedGroups: ['medrender-owner', 'practice-owner', 'standard-user'] },
  { label: 'Configure Practice', path: '/configure', icon: 'Building2', allowedGroups: ['medrender-owner', 'practice-owner'] },
  { label: 'Data Management', path: '/data', icon: 'Database', allowedGroups: ['medrender-owner', 'practice-owner', 'standard-user', 'data_uploader'] },
  { label: 'Data History', path: '/data/history', icon: 'History', allowedGroups: ['medrender-owner', 'practice-owner', 'standard-user', 'data_uploader'] },
  { label: 'Admin', path: '/admin', icon: 'Shield', allowedGroups: ['medrender-owner'] },
  { label: 'User Management', path: '/manage-users', icon: 'Users', allowedGroups: ['medrender-owner', 'practice-owner'] },
];

/**
 * Build nav items dynamically from role definitions.
 * Merges allowedGroups for items with the same path.
 */
export function buildNavItems(roles: RoleDefinition[]): NavItem[] {
  const itemMap = new Map<string, NavItem>();
  for (const role of roles) {
    for (const item of role.navItems) {
      if (!itemMap.has(item.path)) {
        itemMap.set(item.path, {
          label: item.label,
          path: item.path,
          icon: item.icon,
          allowedGroups: [role.roleId],
        });
      } else {
        itemMap.get(item.path)!.allowedGroups.push(role.roleId);
      }
    }
  }
  return Array.from(itemMap.values());
}

/**
 * Returns nav items visible to a user with the given Cognito groups.
 * Accepts an optional navItems parameter, falling back to STATIC_NAV_ITEMS.
 */
export function getNavItemsForGroups(groups: string[], navItems?: NavItem[]): NavItem[] {
  const items = navItems ?? STATIC_NAV_ITEMS;
  return items.filter((item) =>
    item.allowedGroups.some((g) => groups.includes(g))
  );
}

/**
 * Maps a Component_Catalog feature Component's `navPath` to its `componentId`.
 *
 * Only Feature_Card_Components that participate in the per-practice
 * Access_Matrix (`inMatrix: true`) are included. Landing_Surfaces (Dashboard,
 * Data Management — `inMatrix: false`) and non-Component nav items (Configure,
 * Admin, User Management, Data History) are intentionally absent so they retain
 * their existing role-based visibility (Component Access Control, Req 10.2).
 */
const NAV_PATH_TO_COMPONENT_ID: ReadonlyMap<string, string> = new Map(
  COMPONENT_CATALOG.filter((entry) => entry.inMatrix && entry.navPath).map(
    (entry) => [entry.navPath as string, entry.componentId],
  ),
);

/**
 * Filters already role-scoped nav items down to those the caller may actually
 * reach under the per-practice Access_Policy (Req 10.2).
 *
 * For a nav item that corresponds to a feature Component, the entry is shown
 * only if that Component's `componentId` is in `accessibleComponentIds` (the
 * My_Access_Resolver result, which already grants every Component to
 * Practice_Owner / Medrender_Owner). Non-Component nav items (Dashboard, Data,
 * Configure, Admin, User Management) keep their role-based visibility.
 *
 * When `accessibleComponentIds` is undefined (effective access not yet
 * resolved), Component entries are left visible to avoid a flash of an empty
 * sidebar; role-based visibility from {@link getNavItemsForGroups} still
 * applies.
 */
export function filterNavItemsByAccess(
  items: NavItem[],
  accessibleComponentIds: string[] | undefined,
): NavItem[] {
  if (!accessibleComponentIds) return items;
  const accessible = new Set(accessibleComponentIds);
  return items.filter((item) => {
    const componentId = NAV_PATH_TO_COMPONENT_ID.get(item.path);
    if (componentId === undefined) return true; // non-Component nav item
    return accessible.has(componentId);
  });
}
